/**
 * The project's run control in the workspace header, the same on every tab (Home, Preview, Code, API, Data, Traces,
 * History): Start all when something is stopped (its progress popover as on Home), Stop all when everything runs
 * (RunAllActions, `hideDisabled`), and Open app whenever a service runs. The services are the project's, from the pod
 * it runs in (useProjectServices); a viewer gets Open app only. A refused start (the plan's limit) is a short chip whose
 * popover has the message and "Ask for another plan" (RunAllActions `errorAs="popover"`): the header stays one line.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { ArrowSquareOut } from '@phosphor-icons/react';
import useProjectServices from '../../hooks/useProjectServices';
import { servicePath } from '../../utils/projectOverview';
import RunAllActions from './RunAllActions';
import { appItem, openApp, projectRunItems } from './projectRun';
import useDefaultApp from '../../hooks/useDefaultApp';

export default function ProjectRunTools({ projectId, serviceKeys, onOpenLogs = undefined, size = 'middle' }) {
  const { services, canRun, loaded, refresh } = useProjectServices(projectId, { intervalMs: 5000 });
  const { defaultApp, defaultRunnable } = useDefaultApp(projectId);
  if (!projectId || serviceKeys.length === 0 || !loaded) { return null; }
  // The services, with their pinned files' sessions and the default app's file (projectRun projectRunItems).
  const items = projectRunItems(serviceKeys.map((key) => {
    const serviceName = servicePath(key);
    return { serviceName, service: services.find((item) => item.serviceName === serviceName) || null };
  }), services, defaultRunnable);
  // The default app may be a file pinned in a service: its own session, not in the service keys.
  const app = appItem(projectId, items, defaultApp);
  return (
    <span className="project-run-tools" data-testid="project-run-tools">
      {canRun && <RunAllActions errorAs="popover" hideDisabled onChanged={refresh} onOpenLogs={onOpenLogs} projectId={projectId} services={items} size={size} />}
      {app && (
        <Button onClick={() => openApp(projectId, app)} size={size} title="Open the running app in a new tab">
          Open app
          <ArrowSquareOut aria-hidden size={14} />
        </Button>
      )}
    </span>
  );
}

ProjectRunTools.propTypes = {
  onOpenLogs: PropTypes.func,
  projectId: PropTypes.string,
  // The configuration's service keys ('web', 'services/api').
  serviceKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
};
